import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import QRCode from 'qrcode';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { environment } from '../../../environments/environment';
import { AuthService, markDemoSession } from '../../_core/auth.service';
import { IResponse, LoginFlowDestination } from '../../core.interface';

interface DemoCreated {
  requestId: string;
  clientSecret: string;
  expiresIn: number;
  pollIntervalSeconds: number;
}

interface DemoStatus {
  status: string;
  expiresIn: number;
  code?: string | null;
}

@Component({
  selector: 'app-demo-login',
  imports: [
    ButtonModule,
    CardModule,
    InputTextModule,
    MessageModule,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './demo-login.component.html',
  styleUrl: './demo-login.component.scss',
})
export class DemoLoginComponent implements AfterViewInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);

  @ViewChild('qrCanvas') private qrCanvas?: ElementRef<HTMLCanvasElement>;

  protected readonly codeForm = this.formBuilder.group({
    code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });

  protected error = '';
  protected secondsLeft = 0;
  protected busy = false;
  private requestId = '';
  private clientSecret = '';
  private pollHandle = 0;
  private tickHandle = 0;
  private stopped = false;

  constructor() {
    inject(Title).setTitle('Oficina Virtual - Acceso de demostración');
  }

  ngAfterViewInit(): void {
    this.createRequest();
  }

  ngOnDestroy(): void {
    this.stopped = true;
    this.stopTimers();
  }

  protected newQr(): void {
    this.createRequest(this.requestId, this.clientSecret);
  }

  protected redeem(): void {
    const raw = this.codeForm.controls.code.value ?? '';
    const code = raw.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(code)) {
      this.error = 'El código debe tener 6 dígitos';
      return;
    }
    this.busy = true;
    this.error = '';
    this.http
      .post<IResponse<DemoStatus>>(`${environment.apiUrl}/auth/demo-access/redeem`, { code })
      .subscribe({
        next: (response) => this.finishIfAuthorized(response.data),
        error: (err: { error?: { message?: string } }) => {
          this.busy = false;
          this.error = err.error?.message ?? 'El código no es válido';
        },
      });
  }

  private createRequest(previousRequestId?: string, previousClientSecret?: string): void {
    this.stopTimers();
    this.error = '';
    this.http
      .post<IResponse<DemoCreated>>(`${environment.apiUrl}/auth/demo-access/requests`, {
        previousRequestId: previousRequestId || undefined,
        previousClientSecret: previousClientSecret || undefined,
      })
      .subscribe({
        next: (response) => {
          this.requestId = response.data.requestId;
          this.clientSecret = response.data.clientSecret;
          this.secondsLeft = response.data.expiresIn;
          void this.paintQr(this.requestId);
          this.tickHandle = window.setInterval(() => {
            this.secondsLeft = Math.max(this.secondsLeft - 1, 0);
            if (this.secondsLeft === 0) {
              this.stopTimers();
            }
          }, 1000);
          const interval = Math.max(response.data.pollIntervalSeconds, 2) * 1000;
          this.pollHandle = window.setInterval(() => this.poll(), interval);
        },
        error: (err: { error?: { message?: string } }) => {
          this.error = err.error?.message ?? 'No se ha podido preparar el acceso';
        },
      });
  }

  private poll(): void {
    if (!this.requestId || !this.clientSecret || this.busy) {
      return;
    }
    this.http
      .post<IResponse<DemoStatus>>(
        `${environment.apiUrl}/auth/demo-access/requests/${this.requestId}/status`,
        { clientSecret: this.clientSecret },
      )
      .subscribe({
        next: (response) => {
          if (response.data.expiresIn != null) {
            this.secondsLeft = response.data.expiresIn;
          }
          if (response.data.status === 'EXPIRED') {
            this.stopTimers();
            this.error = 'El código ha caducado. Genera uno nuevo.';
            return;
          }
          this.finishIfAuthorized(response.data);
        },
        error: () => {
          this.stopTimers();
          this.error = 'No se ha podido comprobar el acceso';
        },
      });
  }

  private finishIfAuthorized(status: DemoStatus): void {
    if (status.status !== 'AUTHORIZED' || !status.code || this.stopped) {
      return;
    }
    this.busy = true;
    this.stopTimers();
    this.clientSecret = '';
    this.auth.ssoExchange(status.code).subscribe({
      next: async () => {
        markDemoSession();
        const destination = await this.auth.completeLoginFlow();
        this.navigateAfterLogin(destination);
      },
      error: (err: { error?: { message?: string } }) => {
        this.busy = false;
        this.error = err.error?.message ?? 'No se ha podido iniciar la sesión de demostración';
      },
    });
  }

  private navigateAfterLogin(destination: LoginFlowDestination): void {
    if (destination === 'CHANGE_PASSWORD') {
      void this.router.navigate(['/', 'change-password'], { replaceUrl: true });
      return;
    }
    if (destination === 'LOADING') {
      void this.router.navigate(['/', 'loading'], { replaceUrl: true });
      return;
    }
    void this.router.navigate(['/', 'propiedades'], { replaceUrl: true });
  }

  private async paintQr(value: string): Promise<void> {
    const canvas = this.qrCanvas?.nativeElement;
    if (!canvas) {
      return;
    }
    await QRCode.toCanvas(canvas, value, { width: 220, margin: 1 });
  }

  private stopTimers(): void {
    window.clearInterval(this.pollHandle);
    window.clearInterval(this.tickHandle);
    this.pollHandle = 0;
    this.tickHandle = 0;
  }
}
